"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { formatBytes } from "@/lib/tool-utils";

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_FILE_BYTES = 50 * 1024 * 1024; // 50 MB per file
const MAX_CANVAS_SIDE = 16384; // safe canvas limit across browsers

type ItemStatus = "queued" | "compressing" | "done" | "error";

type QueueItem = {
  id: string;
  file: File;
  originalSize: number;
  compressedSize: number | null;
  downloadUrl: string;
  downloadName: string;
  status: ItemStatus;
  error?: string;
  /** Settings the result was created with, so we can tell when it is out of date. */
  settingsKey?: string;
};

export default function ImageCompressor({}: { toolName: string }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const queueRef = useRef<QueueItem[]>([]);

  const [quality, setQuality] = useState(0.8);
  const [maxWidth, setMaxWidth] = useState("");
  const [maxHeight, setMaxHeight] = useState("");
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessingAll, setIsProcessingAll] = useState(false);
  const [notice, setNotice] = useState("");

  // Keep a ref in sync so async loops always see the latest queue.
  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  // Revoke every blob URL when the tool unmounts.
  useEffect(() => {
    return () => {
      queueRef.current.forEach((item) => {
        if (item.downloadUrl) URL.revokeObjectURL(item.downloadUrl);
      });
    };
  }, []);

  const settingsKey = `${quality}|${maxWidth}|${maxHeight}`;

  const updateItem = useCallback((id: string, patch: Partial<QueueItem>) => {
    setQueue((previous) =>
      previous.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)),
    );
  }, []);

  function addFiles(nextFiles: FileList | File[]) {
    const all = Array.from(nextFiles);
    const accepted: File[] = [];
    const rejected: string[] = [];

    for (const file of all) {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        rejected.push(`${file.name} (unsupported type)`);
      } else if (file.size > MAX_FILE_BYTES) {
        rejected.push(`${file.name} (over ${formatBytes(MAX_FILE_BYTES)})`);
      } else {
        accepted.push(file);
      }
    }

    if (rejected.length > 0) {
      setNotice(
        `Skipped ${rejected.length} file${rejected.length === 1 ? "" : "s"}: ${rejected.join(", ")}. Only JPG, PNG and WebP are supported.`,
      );
    } else {
      setNotice("");
    }

    if (accepted.length === 0) return;

    setQueue((previous) => [
      ...previous,
      ...accepted.map((file) => ({
        id: `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        originalSize: file.size,
        compressedSize: null,
        downloadUrl: "",
        downloadName: buildDownloadName(file),
        status: "queued" as const,
      })),
    ]);
  }

  async function compressQueueItem(item: QueueItem) {
    updateItem(item.id, { status: "compressing", error: undefined });

    try {
      const blob = await compressImage(item.file, quality, maxWidth, maxHeight);
      const downloadUrl = URL.createObjectURL(blob);

      // Release the previous result, if this is a re-compress.
      if (item.downloadUrl) URL.revokeObjectURL(item.downloadUrl);

      updateItem(item.id, {
        status: "done",
        compressedSize: blob.size,
        downloadUrl,
        settingsKey,
      });
    } catch (error) {
      updateItem(item.id, {
        status: "error",
        error:
          error instanceof Error
            ? error.message
            : "Something went wrong while compressing this file.",
      });
    }
  }

  async function compressAll() {
    setIsProcessingAll(true);
    setNotice("");

    // Snapshot ids now; read each item fresh from the ref inside the loop.
    const ids = queueRef.current
      .filter((item) => item.status !== "compressing")
      .filter((item) => item.status !== "done" || item.settingsKey !== settingsKey)
      .map((item) => item.id);

    for (const id of ids) {
      const current = queueRef.current.find((entry) => entry.id === id);
      if (current) await compressQueueItem(current);
    }

    setIsProcessingAll(false);
  }

  function removeQueueItem(id: string) {
    setQueue((previous) => {
      const target = previous.find((entry) => entry.id === id);
      if (target?.downloadUrl) URL.revokeObjectURL(target.downloadUrl);
      return previous.filter((entry) => entry.id !== id);
    });
  }

  function clearAll() {
    queue.forEach((item) => {
      if (item.downloadUrl) URL.revokeObjectURL(item.downloadUrl);
    });
    setQueue([]);
    setNotice("");
  }

  function downloadAll() {
    // Stagger clicks slightly so browsers don't block multiple downloads.
    queue
      .filter((item) => item.status === "done" && item.downloadUrl)
      .forEach((item, index) => {
        window.setTimeout(() => {
          const link = document.createElement("a");
          link.href = item.downloadUrl;
          link.download = item.downloadName;
          link.click();
        }, index * 250);
      });
  }

  const doneItems = queue.filter((item) => item.status === "done" && item.compressedSize !== null);
  const totalBefore = doneItems.reduce((sum, item) => sum + item.originalSize, 0);
  const totalAfter = doneItems.reduce((sum, item) => sum + (item.compressedSize ?? 0), 0);
  const totalSaved = totalBefore - totalAfter;
  const pendingCount = queue.filter(
    (item) =>
      item.status === "queued" ||
      item.status === "error" ||
      (item.status === "done" && item.settingsKey !== settingsKey),
  ).length;

  return (
    <div className="space-y-4">
      <div
        role="button"
        tabIndex={0}
        aria-label="Add images: click to browse or drag files here"
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        onDragEnter={(event) => {
          event.preventDefault();
          dragDepth.current += 1;
          setIsDragging(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={() => {
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setIsDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          dragDepth.current = 0;
          setIsDragging(false);
          addFiles(event.dataTransfer.files);
        }}
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-6 text-center outline-none transition-colors focus-visible:ring-2 focus-visible:ring-indigo-300 ${
          isDragging
            ? "border-indigo-500 bg-indigo-50"
            : "border-slate-300 bg-slate-50 hover:border-indigo-400"
        }`}
      >
        <p className="text-base font-semibold text-slate-900">
          {isDragging ? "Drop to add images" : "Drop JPG, PNG or WebP images here"}
        </p>
        <p className="mt-2 text-sm text-slate-600">
          Or click anywhere in this box to choose files. You can add several at once.
        </p>
        <span className="mt-4 inline-block rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white">
          Choose files
        </span>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(event) => {
            if (event.target.files) {
              addFiles(event.target.files);
              event.target.value = "";
            }
          }}
        />
      </div>

      {notice && (
        <p
          role="alert"
          className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
        >
          {notice}
        </p>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <label className="text-sm font-medium text-slate-700">
          Quality
          <input
            type="range"
            min="0.1"
            max="1"
            step="0.05"
            value={quality}
            onChange={(event) => setQuality(Number(event.target.value))}
            className="mt-2 w-full"
          />
          <span className="text-xs text-slate-500">
            {Math.round(quality * 100)}% · applies to JPG and WebP. PNG is lossless, so shrink it with max width or height.
          </span>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm font-medium text-slate-700">
            Max width (px)
            <input
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              value={maxWidth}
              onChange={(event) => setMaxWidth(event.target.value)}
              placeholder="Optional"
              className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
            />
          </label>
          <label className="text-sm font-medium text-slate-700">
            Max height (px)
            <input
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              value={maxHeight}
              onChange={(event) => setMaxHeight(event.target.value)}
              placeholder="Optional"
              className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
            />
          </label>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void compressAll()}
          disabled={pendingCount === 0 || isProcessingAll}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {isProcessingAll
            ? "Compressing…"
            : pendingCount > 0
              ? `Compress ${pendingCount} image${pendingCount === 1 ? "" : "s"}`
              : "Compress all"}
        </button>

        {doneItems.length > 1 && (
          <button
            type="button"
            onClick={downloadAll}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
          >
            Download all ({doneItems.length})
          </button>
        )}

        {queue.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            disabled={isProcessingAll}
            className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Clear all
          </button>
        )}

        <span className="rounded-full bg-slate-100 px-3 py-2 text-xs font-medium text-slate-700">
          100% private — processed in your browser
        </span>
      </div>

      {doneItems.length > 0 && (
        <p
          aria-live="polite"
          className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
        >
          {totalSaved > 0
            ? `Saved ${formatBytes(totalSaved)} across ${doneItems.length} image${doneItems.length === 1 ? "" : "s"} (${formatBytes(totalBefore)} → ${formatBytes(totalAfter)}, ${percentSaved(totalBefore, totalAfter)}% smaller).`
            : `Processed ${doneItems.length} image${doneItems.length === 1 ? "" : "s"}, but the results aren't smaller. Try a lower quality or a smaller max width.`}
        </p>
      )}

      <div className="space-y-3">
        {queue.map((item) => {
          const outdated = item.status === "done" && item.settingsKey !== settingsKey;
          const isLarger =
            item.compressedSize !== null && item.compressedSize >= item.originalSize;

          return (
            <div key={item.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-900">{item.file.name}</p>
                  <p className="text-xs text-slate-500">
                    Before: {formatBytes(item.originalSize)}
                    {item.compressedSize !== null && (
                      <>
                        <span className="ml-2">After: {formatBytes(item.compressedSize)}</span>
                        <span
                          className={`ml-2 font-semibold ${
                            isLarger ? "text-amber-600" : "text-emerald-600"
                          }`}
                        >
                          {isLarger
                            ? "No savings"
                            : `−${percentSaved(item.originalSize, item.compressedSize)}%`}
                        </span>
                      </>
                    )}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => void compressQueueItem(item)}
                    disabled={item.status === "compressing" || (item.status === "done" && !outdated)}
                    className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                  >
                    {item.status === "compressing"
                      ? "Compressing…"
                      : item.status === "done"
                        ? outdated
                          ? "Re-compress"
                          : "Done"
                        : item.status === "error"
                          ? "Retry"
                          : "Compress"}
                  </button>
                  {item.downloadUrl && (
                    <a
                      href={item.downloadUrl}
                      download={item.downloadName}
                      className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-700"
                    >
                      Download
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => removeQueueItem(item.id)}
                    disabled={item.status === "compressing"}
                    aria-label={`Remove ${item.file.name}`}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              </div>

              {outdated && (
                <p className="mt-2 text-xs text-slate-500">
                  Settings changed since this was compressed. Re-compress to apply them.
                </p>
              )}
              {isLarger && item.status === "done" && (
                <p className="mt-2 text-xs text-amber-700">
                  This file was already well optimized. Lower the quality or set a max width to shrink it further.
                </p>
              )}
              {item.status === "error" && item.error && (
                <p role="alert" className="mt-2 text-xs text-rose-600">
                  {item.error}
                </p>
              )}
            </div>
          );
        })}

        {queue.length === 0 && (
          <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
            Your queued files will appear here once you add them.
          </p>
        )}
      </div>
    </div>
  );
}

function percentSaved(before: number, after: number) {
  if (before <= 0) return 0;
  return Math.max(0, Math.round(((before - after) / before) * 100));
}

function buildDownloadName(file: File) {
  const dot = file.name.lastIndexOf(".");
  const stem = dot > 0 ? file.name.slice(0, dot) : file.name;
  const ext =
    file.type === "image/png" ? "png" : file.type === "image/webp" ? "webp" : "jpg";
  return `${stem}-compressed.${ext}`;
}

function loadImage(file: File) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Unable to read this image. The file may be corrupted."));
    };
    image.src = objectUrl;
  });
}

async function compressImage(
  file: File,
  quality: number,
  maxWidth: string,
  maxHeight: string,
) {
  const sourceImage = await loadImage(file);
  const maxWidthValue = Math.floor(Number(maxWidth) || 0);
  const maxHeightValue = Math.floor(Number(maxHeight) || 0);

  let width = sourceImage.naturalWidth || sourceImage.width;
  let height = sourceImage.naturalHeight || sourceImage.height;

  if (maxWidthValue > 0 || maxHeightValue > 0) {
    const ratioWidth = maxWidthValue > 0 ? maxWidthValue / width : 1;
    const ratioHeight = maxHeightValue > 0 ? maxHeightValue / height : 1;
    const scale = Math.min(ratioWidth, ratioHeight, 1); // never upscale
    width = Math.max(1, Math.round(width * scale));
    height = Math.max(1, Math.round(height * scale));
  }

  if (width > MAX_CANVAS_SIDE || height > MAX_CANVAS_SIDE) {
    throw new Error(
      `This image is too large to process in the browser (${width}×${height}px). Set a smaller max width or height.`,
    );
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas is not supported in this browser.");
  }

  const mimeType =
    file.type === "image/png"
      ? "image/png"
      : file.type === "image/webp"
        ? "image/webp"
        : "image/jpeg";

  // JPEG has no transparency: paint white first so transparent areas don't turn black.
  if (mimeType === "image/jpeg") {
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
  }

  context.imageSmoothingQuality = "high";
  context.drawImage(sourceImage, 0, 0, width, height);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(
      (result) => resolve(result),
      mimeType,
      mimeType === "image/png" ? undefined : quality,
    );
  });

  if (!blob) {
    throw new Error("The browser could not generate a compressed image.");
  }

  return blob;
}